import { DeleteRounded, InfoRounded } from '@mui/icons-material'
import { Box, Button, Chip, Divider, IconButton, MenuItem, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { memo, ReactNode, useEffect } from 'react'
import { IFlow, INode } from '../../../../definitions/Nodes'
import { IAcceptedData } from '../../../../definitions/NodesDescription'
import { NodeDataMap } from '../../../../definitions/NodeTypes'
import NodeCodemirrorTextEditor from '../NodeCodemirrorTextEditor'
import NodeManager from '../../../../managers/NodeManager'
import { autocompletion, Completion, CompletionContext } from '@codemirror/autocomplete'

interface P {
    acceptedData: IAcceptedData<'xfrm_filterby'>
    selectedNode: INode<'xfrm_filterby'>
    flow: IFlow
    setSelected: (node: INode<'xfrm_filterby'>) => void
    disable: boolean
}

const operators = {
    'Equal': '=',
    'Not equal': '!=',
    'Greater than': '>',
    'Greater than or equal to': '>=',
    'Less than': '<',
    'Less than or equal to': '<=',
    'Includes': 'includes'
}

const positions = {
    'First row': 'first',
    'Last row': 'last',
    'Row number': 'index',
    'All rows': 'all'
}

const NodeFilterByEditor = ({ selectedNode, setSelected, disable, flow }: P) => {
    const theme = useTheme()

    useEffect(() => {
        if (!selectedNode.data?.filterData) {
            setSelected({
                ...selectedNode,
                data: {
                    ...selectedNode.data,
                    filterData: {
                        ...selectedNode.data.filterData,
                        conditions: [],
                        logicalOperator: 'AND',
                        pick: { type: 'all' }
                    }
                }
            })
        }
    }, [])

    const renderInfoField = (title: string | ReactNode) => (
        <Tooltip arrow title={title} disableHoverListener={disable}>
            <InfoRounded fontSize='inherit' sx={{ cursor: disable ? undefined : 'pointer', color: disable ? 'var(--icon-disabled)' : 'var(--icon-color)' }} />
        </Tooltip>
    )

    const handleLogicalOpChange = (type: NodeDataMap['xfrm_filterby']['filterData']['logicalOperator']) => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                filterData: {
                    ...selectedNode.data.filterData,
                    logicalOperator: type
                }
            }
        })
    }

    const handleInputChange = (event: any, index: number) => {
        const { name, value } = event.target

        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                filterData: {
                    ...selectedNode.data.filterData,
                    conditions: selectedNode.data.filterData.conditions.map((c, i) => (i === index ? { ...c, [name]: value } : c))
                }
            }
        })
    }

    const handleCmInpuntChange = (value: string, index: number) => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                filterData: {
                    ...selectedNode.data.filterData,
                    conditions: selectedNode.data.filterData.conditions.map((c, i) => (i === index ? { ...c, value } : c))
                }
            }
        })
    }

    const handlePickChange = (event: any) => {
        const { name, value } = event.target
        let newValue: string | number = value

        if (name === 'type')
            newValue = value

        if (name === 'value') {
            if (value === '')
                newValue = ''
            else {
                const num = Number(value)
                if (!isNaN(num))
                    newValue = num
                else
                    newValue = ''
            }
        }

        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                filterData: {
                    ...selectedNode.data.filterData,
                    pick: {
                        ...selectedNode.data.filterData.pick,
                        [name]: newValue
                    }
                }
            }
        })
    }

    const handleAddCondition = () => {
        const clone = structuredClone(selectedNode.data.filterData.conditions)
        clone.push({ key: '', operator: '=', value: '' })
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                filterData: {
                    ...selectedNode.data.filterData,
                    conditions: clone
                }
            }
        })
    }

    const handleRemoveCondition = (index: number) => {
        setSelected({
            ...selectedNode,
            data: {
                ...selectedNode.data,
                filterData: {
                    ...selectedNode.data.filterData,
                    conditions: selectedNode.data.filterData.conditions.filter((_, i) => i !== index)
                }
            }
        })
    }

    const inputDataShape = NodeManager.getMappedShapes(NodeManager.getInputShapes<'xfrm_filterby'>(selectedNode, flow.nodes).find(s => s.argKey === 'items'))
    const filterDataShape = NodeManager.getMappedShapes(NodeManager.getInputShapes<'xfrm_filterby'>(selectedNode, flow.nodes).find(s => s.argKey === 'filter'))
    const globalsDataShape = flow?.globals ? Object.keys(flow.globals) : []

    const inputDataSuggestions: Completion[] = filterDataShape.length > 0
        ? filterDataShape.map(x => ({
            label: x,
            type: 'fieldInput',
            info: '-> Field Input',
            apply: `@{${x}}`
        }))
        : []

    const globalsSuggestions: Completion[] = globalsDataShape.length > 0
        ? globalsDataShape.map(x => ({
            label: `globals.${x}`,
            type: 'fieldInput',
            info: '-> Field Input',
            apply: `@{globals.${x}}`
        }))
        : []

    const myCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: [...inputDataSuggestions, ...globalsSuggestions]
        }
    }

    return (
        <Stack spacing={3}>
            <Stack flexDirection='row' justifyContent='space-between'>
                <Stack flexDirection='row' gap={1.5} alignItems='center'>
                    {renderInfoField('')}
                    <Typography variant='subtitle2'>Filter list:</Typography>
                </Stack>
                <Stack flexDirection='row' justifyContent='center' gap={1.5}>
                    <Chip
                        disabled={disable || selectedNode.data.filterData?.conditions.length === 0}
                        label='and'
                        color={(selectedNode.data.filterData?.conditions.length > 0 && selectedNode.data.filterData?.logicalOperator === 'AND') ? 'secondary' : undefined}
                        clickable
                        onClick={() => handleLogicalOpChange('AND')}
                    />
                    <Chip
                        disabled={disable || selectedNode.data.filterData?.conditions.length === 0}
                        label='or'
                        color={(selectedNode.data.filterData?.conditions.length > 0 && selectedNode.data.filterData?.logicalOperator === 'OR') ? 'warning' : undefined}
                        clickable
                        onClick={() => handleLogicalOpChange('OR')}
                    />
                </Stack>
            </Stack>
            <Stack spacing={1.5}>
                {selectedNode.data.filterData?.conditions.map((condition, idx) => (
                    <Stack key={idx} flexDirection='row' gap={1.5} alignItems='center' width='100%'>
                        <Typography variant='caption' color={theme.palette.text.secondary} textTransform='lowercase' width={96} overflow='hidden' visibility={idx === 0 ? 'hidden' : undefined}>{selectedNode.data.filterData?.logicalOperator}</Typography>
                        <TextField
                            name='key'
                            disabled={disable}
                            select={inputDataShape.length > 0}
                            placeholder='Key'
                            size='small'
                            fullWidth
                            value={condition.key}
                            onChange={e => handleInputChange(e, idx)}>
                            {inputDataShape.length > 0 && inputDataShape.map((v, i) => (
                                <MenuItem key={`${i}-${v}`} value={v}>
                                    {v}
                                </MenuItem>
                            )
                            )}
                        </TextField>
                        <TextField
                            name='operator'
                            disabled={disable}
                            placeholder='Operation'
                            size='small'
                            select
                            fullWidth
                            value={condition.operator}
                            onChange={e => handleInputChange(e, idx)}>
                            {Object.entries(operators).map(([k, v]) => (
                                <MenuItem key={k} value={v}>
                                    {k.toUpperCase()}
                                </MenuItem>
                            )
                            )}
                        </TextField>
                        <NodeCodemirrorTextEditor
                            value={condition.value}
                            disabled={disable}
                            placeholder='value'
                            style={{ overflow: 'hidden', minWidth: '100%' }}
                            onChange={value => handleCmInpuntChange(value, idx)}
                            extensions={[autocompletion({ override: [myCompletions] })]}
                        />
                        <Box>
                            <IconButton
                                size='small'
                                disabled={disable}
                                onClick={() => handleRemoveCondition(idx)}>
                                <DeleteRounded color='error' fontSize='small' />
                            </IconButton>
                        </Box>
                    </Stack>
                ))}
                <Button
                    variant='text'
                    disabled={disable}
                    onClick={() => handleAddCondition()}>
                    Add new condition
                </Button>
            </Stack>

            <Divider />

            <Stack flexDirection='row' gap={1.5} alignItems='center'>
                {renderInfoField('')}
                <Typography variant='subtitle2'>Select one specific position:</Typography>
            </Stack>
            <Stack spacing={1.5}>
                <Stack flexDirection='row' gap={1.5} alignItems='flex-start' width='100%'>
                    <Stack flexDirection='row' alignItems='center' gap={1.5}>
                        <Typography variant='body2'>Select </Typography>
                        <TextField
                            name='type'
                            disabled={disable}
                            size='small'
                            select
                            fullWidth
                            value={selectedNode.data.filterData?.pick?.type ?? 'All rows'}
                            onChange={e => handlePickChange(e)}>
                            {Object.entries(positions).map(([k, v]) => (
                                <MenuItem key={k} value={v}>
                                    {k.toUpperCase()}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                    <TextField
                        name='value'
                        helperText={selectedNode.data.filterData?.pick?.type === 'index' ? 'Based on the filtered table order' : undefined}
                        placeholder={selectedNode.data.filterData?.pick?.type === 'index' ? 'e.g. 24' : 'No data needed'}
                        disabled={disable || !selectedNode.data.filterData?.pick || selectedNode.data.filterData?.pick?.type === 'all' || selectedNode.data.filterData?.pick?.type === 'first' || selectedNode.data.filterData?.pick?.type === 'last'}
                        size='small'
                        fullWidth
                        value={selectedNode.data.filterData?.pick?.type === 'index' ? selectedNode.data.filterData?.pick?.value : ''}
                        onChange={e => handlePickChange(e)}
                    />
                </Stack>
            </Stack>
        </Stack>
    )
}

export default memo(NodeFilterByEditor)
